import dayjs, { Dayjs } from "utils/dayjs"

import { Guid, IApiLink } from "@/types"
import ReservationTime, { IReservationTimeDto } from "./ReservationTime"
import Address, { IAddressDto } from "./Address"
import { Purpose, ApiLinkEntity, ApiLinkRel, InvoicingType, Currency } from "config/constants"
import Location, { ILocationDto } from "./Location"
import ReservationCartArticle, { IGetReservationCartArticleDto } from "./ReservationCartArticle"
import { printPeriod } from "utils/utils"
import Reservation, { ReservationStatus, IRentToBuy, IReservationRentToBuy, ReferenceNumbersUpdateType, IGetReservationReferenceNumbers, InvoiceIntervalRule } from "./Reservation"
import DowntimePeriod, { IGetDowntimePeriodDto } from "./DowntimePeriod"
import ReservationCartAsset, { IReservationCartAssetWithInvalidPeriod } from "./ReservationCartAsset"
import ReservationCartInvoicePosition, { IGetReservationCartInvoicePositionDto } from "./ReservationCartInvoicePosition"
import CustomerExtended, { ICustomerExtendedDto } from "./CustomerExtended"
import { ICustomerDto } from "./Customer"
import CalculationAdjustment, { IGetCalculationAdjustmentDto } from "./CalculationAdjustment"
import getCustomerBasedCurrency from "utils/getCustomerBasedCurrency"
import InvoiceInterval, { IGetInvoiceIntervalDto } from "./InvoiceInterval"
import { InvoiceRateCalculationType } from "./OutgoingInvoice"

export enum ReservationCartState {
    incomplete = "incomplete",
    fixedUnavailable = "fixedUnavailable",
    fixedRequired = "fixedRequired",
    valid = "valid",
    shortage = "shortage",
}

export enum ReservationCartCopyFlag {
    fixedAssets = "fixedAssets",
    all = "all",
}

export interface IUpdateReservationCartReferenceNumbers {
    updateType: ReferenceNumbersUpdateType
    numberSale?: string | null
    numberRental?: string | null
    costUnit?: string | null
    useFirstAssetOfConfiguration?: boolean
}

interface IReservationCartStateErrors {
    purposeIdRequired: boolean
    eventLocationRequired: boolean
    eventAddressRequired: boolean
    customerNameRequired: boolean
    customerOrderDateRequired: boolean
    salesPersonNumberRequired: boolean
    salesAdminNumberRequired: boolean
    termsOfDeliveryIdRequired: boolean
    invoicePositionsRequired: boolean
    invoicePositionsOutOfReservationDates: boolean
    erpCustomerRequired: boolean
    rentToBuyLinkInvalid: boolean
    profitCenterIdRequired: boolean
    closeRequestOpenInvoicePeriods: boolean
}

export interface IGetReservationCartDto {
    id: Guid
    number?: string | null
    reservationId?: Guid | null
    articleId: Guid
    reservationStatus: ReservationStatus
    cartState: ReservationCartState
    cartStateErrors?: IReservationCartStateErrors | null
    articles: IGetReservationCartArticleDto[]
    reservationTime: IReservationTimeDto
    reservationAddress?: IAddressDto
    recipientName?: string | null
    constructionSite?: string | null
    personInCharge?: string | null
    personInChargePhone?: string | null
    purpose?: Purpose
    language: string
    warehouseId?: Guid | null
    customer?: ICustomerExtendedDto
    currency?: Currency | null
    printCustomerContactName: boolean
    configurationCompleted?: boolean
    reservationLocation?: ILocationDto
    comment?: string
    invoicePositions: IGetReservationCartInvoicePositionDto[]
    calculationAdjustments: IGetCalculationAdjustmentDto[]
    shippingDetails?: string
    salesPersonNumber?: string
    salesAdminNumber?: string
    crmReferenceNumber?: string
    customerOrderNumber?: string
    customerOrderDate?: string
    deliveryTermId?: string
    headerSectionConfirmationText?: string | null
    footerSectionConfirmationText?: string | null
    rentalContractHeaderText?: string | null
    rentalContractFooterText?: string | null
    invoiceHeaderText?: string | null
    invoiceFooterText?: string | null
    deliveryNoteHeaderText?: string | null
    deliveryNoteFooterText?: string | null
    downtimePeriods?: IGetDowntimePeriodDto[]
    readyForShipping: boolean
    orderConfirmed: boolean
    readyForReturn: boolean
    orderClosed: boolean
    termsOfPaymentCode?: string | null
    orderTypeId?: Guid | null
    originCountryCode?: string
    targetCountryCode?: string
    invoiceInAdvanceAllowed?: boolean | null
    orderAcknowledgementWillBeRequested: boolean
    referenceNumbers: IGetReservationReferenceNumbers
    rentToBuy: IReservationRentToBuy
    commission?: number | null
    profitCenterId?: Guid | null
    automaticInvoicing?: boolean | null
    invoiceRateCalculationType?: InvoiceRateCalculationType | null
    invoiceBeforeShipping?: boolean | null
    useNotReleasedAssets: boolean
    invoicingType?: InvoicingType | null
    invoicing: boolean
    workingDaysCalendarId?: Guid | null
    workingDaysPerMonth?: number | null
    invoiceIntervalRule?: InvoiceIntervalRule | null
    invoiceIntervalRuleCustom?: boolean | null
    invoiceIntervals: IGetInvoiceIntervalDto[]
    invoiceBlocked: boolean
    invoiceBlockedBy?: Guid | null
    invoiceBlockedDate?: string | null
    invoiceBlockedReason?: string | null
    machineInsuranceIncluded?: boolean | null
    machineInsuranceExcess?: number | null
    links?: IApiLink[]
}

export interface ICreateReservationCartDto {
    reservationId?: Guid
    copyFlag?: ReservationCartCopyFlag
    articleId?: Guid
    assetId?: Guid
    offerId?: Guid
    reservationTime?: IReservationTimeDto
    overrideOwnership?: boolean
}

export interface IUpdateReservationCartDto {
    reservationTime?: IReservationTimeDto
    purpose?: Purpose
    language?: string
    warehouseId?: Guid | null
    location?: ILocationDto
    customer?: ICustomerDto
    currency?: Currency
    printCustomerContactName?: boolean
    configurationCompleted?: boolean
    reservationAddress?: IAddressDto
    recipientName?: string | null
    constructionSite?: string | null
    personInCharge?: string | null
    personInChargePhone?: string | null
    comment?: string
    shippingDetails?: string
    salesPersonNumber?: string | null
    salesAdminNumber?: string | null
    crmReferenceNumber?: string
    customerOrderNumber?: string
    customerOrderDate?: string | null
    deliveryTermId?: string | null
    headerSectionConfirmationText?: string | null
    footerSectionConfirmationText?: string | null
    rentalContractHeaderText?: string | null
    rentalContractFooterText?: string | null
    invoiceHeaderText?: string | null
    invoiceFooterText?: string | null
    deliveryNoteHeaderText?: string | null
    deliveryNoteFooterText?: string | null
    readyForShipping?: boolean
    orderConfirmed?: boolean
    readyForReturn?: boolean
    orderClosed?: boolean
    termsOfPaymentCode?: string
    orderTypeId?: Guid
    originCountryCode?: string
    targetCountryCode?: string
    invoicingType?: InvoicingType | null
    automaticInvoicing?: boolean
    invoiceRateCalculationType?: InvoiceRateCalculationType | null
    invoiceBeforeShipping?: boolean
    useNotReleasedAssets?: boolean
    orderAcknowledgementWillBeRequested?: boolean | null
    referenceNumbers?: IUpdateReservationCartReferenceNumbers | null
    commission?: number | null
    rentToBuy?: IRentToBuy | null
    profitCenterId?: Guid | null
    forceReevaluate?: boolean
    invoicing?: boolean
    workingDaysCalendarId?: Guid | null
    workingDaysPerMonth?: number | null
    invoiceIntervalRule?: InvoiceIntervalRule | null
    invoiceBlocked?: boolean
    invoiceBlockedReason?: string | null
    machineInsuranceIncluded?: boolean | null
    machineInsuranceExcess?: number | null
}

export const RESERVATION_INVOICE_TEXT_LINE_BREAK_AFTER = 55

export default class ReservationCart {
    id: Guid
    number?: string
    reservationId?: Guid
    articleId: Guid
    reservationStatus: ReservationStatus
    cartState: ReservationCartState
    cartStateErrors?: IReservationCartStateErrors
    articles: ReservationCartArticle[]
    reservationTime: ReservationTime
    reservationAddress?: Address
    recipientName?: string
    constructionSite?: string
    personInCharge?: string
    personInChargePhone?: string
    purpose?: Purpose
    language: string
    warehouseId?: Guid
    customer?: CustomerExtended
    currency?: Currency
    printCustomerContactName: boolean
    configurationCompleted?: boolean
    reservationLocation?: Location
    comment?: string
    invoicePositions: ReservationCartInvoicePosition[]
    calculationAdjustments: CalculationAdjustment[]
    shippingDetails?: string
    salesPersonNumber?: string
    salesAdminNumber?: string
    crmReferenceNumber?: string
    customerOrderNumber?: string
    customerOrderDate?: Dayjs
    deliveryTermId?: string
    headerSectionConfirmationText?: string
    footerSectionConfirmationText?: string
    rentalContractHeaderText?: string
    rentalContractFooterText?: string
    invoiceHeaderText?: string
    invoiceFooterText?: string
    deliveryNoteHeaderText?: string
    deliveryNoteFooterText?: string
    downtimePeriods: DowntimePeriod[]
    readyForShipping: boolean
    orderConfirmed: boolean
    readyForReturn: boolean
    orderClosed: boolean
    termsOfPaymentCode?: string
    orderTypeId?: Guid
    originCountryCode?: string
    targetCountryCode?: string
    automaticInvoicing: boolean | null
    invoiceRateCalculationType?: InvoiceRateCalculationType
    invoiceBeforeShipping: boolean | null
    useNotReleasedAssets: boolean
    invoicingType: InvoicingType | null
    orderAcknowledgementWillBeRequested: boolean
    referenceNumbers: IGetReservationReferenceNumbers
    rentToBuy: IReservationRentToBuy
    commission: number | null
    profitCenterId?: Guid
    invoicing: boolean
    workingDaysCalendarId: Guid | null
    workingDaysPerMonth: number | null
    invoiceIntervalRule?: InvoiceIntervalRule
    invoiceIntervalRuleCustom: boolean
    invoiceIntervals: InvoiceInterval[]
    invoiceBlocked: boolean
    invoiceBlockedBy?: Guid
    invoiceBlockedDate?: Dayjs
    invoiceBlockedReason?: string
    machineInsuranceIncluded: boolean
    machineInsuranceExcess?: number

    initialReservationStatus?: ReservationStatus
    initialCommission?: number | null
    initialCurrency?: Currency | null
    currencyChangedWhileHavingInvoicePositions?: boolean

    linkUpdate?: IApiLink
    linkDelete?: IApiLink
    linkCancel?: IApiLink
    linkUpdateArticle?: IApiLink
    linkPatchArticle?: IApiLink
    linkAddAssets?: IApiLink
    linkUpdateAsset?: IApiLink
    linkDeleteAsset?: IApiLink
    linkEmptyConfiguration?: IApiLink
    linkTouch?: IApiLink
    linkGetShippingOrders?: IApiLink
    linkCreateDowntimePeriod?: IApiLink
    linkCreateInvoicePosition?: IApiLink
    linkCreateCalculationAdjustment?: IApiLink
    linkGetReservationCartConflicts?: IApiLink

    constructor(dto: IGetReservationCartDto) {
        this.id = dto.id
        this.number = dto.number ?? undefined
        this.reservationId = dto.reservationId ?? undefined
        this.articleId = dto.articleId
        this.reservationTime = new ReservationTime(dto.reservationTime)
        this.reservationAddress = dto.reservationAddress ? new Address(dto.reservationAddress) : undefined
        this.recipientName = dto.recipientName ?? undefined
        this.constructionSite = dto.constructionSite ?? undefined
        this.personInCharge = dto.personInCharge ?? undefined
        this.personInChargePhone = dto.personInChargePhone ?? undefined
        this.purpose = dto.purpose
        this.language = dto.language
        this.warehouseId = dto.warehouseId ?? undefined
        this.customer = dto.customer?.name ? new CustomerExtended(dto.customer) : undefined
        this.currency = dto.currency ?? undefined
        this.printCustomerContactName = dto.printCustomerContactName
        this.configurationCompleted = dto.configurationCompleted
        this.reservationLocation = dto.reservationLocation ? new Location(dto.reservationLocation) : undefined
        this.comment = dto.comment
        this.invoicePositions = dto.invoicePositions && this.purpose
            ? dto.invoicePositions.map(ipDto => new ReservationCartInvoicePosition({
                dto: ipDto,
                purpose: this.purpose!
            }))
            : []
        this.calculationAdjustments = dto.calculationAdjustments?.map(caDto => new CalculationAdjustment(caDto)) ?? []
        this.shippingDetails = dto.shippingDetails ?? undefined
        this.reservationStatus = dto.reservationStatus
        this.cartState = dto.cartState
        this.cartStateErrors = dto.cartStateErrors ?? undefined
        this.salesPersonNumber = dto.salesPersonNumber
        this.salesAdminNumber = dto.salesAdminNumber
        this.crmReferenceNumber = dto.crmReferenceNumber
        this.customerOrderNumber = dto.customerOrderNumber
        this.customerOrderDate = dto.customerOrderDate ? dayjs(dto.customerOrderDate, "YYYY-MM-DD") : undefined
        this.deliveryTermId = dto.deliveryTermId
        this.headerSectionConfirmationText = dto.headerSectionConfirmationText ?? undefined
        this.footerSectionConfirmationText = dto.footerSectionConfirmationText ?? undefined
        this.rentalContractHeaderText = dto.rentalContractHeaderText ?? undefined
        this.rentalContractFooterText = dto.rentalContractFooterText ?? undefined
        this.invoiceHeaderText = dto.invoiceHeaderText ?? undefined
        this.invoiceFooterText = dto.invoiceFooterText ?? undefined
        this.deliveryNoteHeaderText = dto.deliveryNoteHeaderText ?? undefined
        this.deliveryNoteFooterText = dto.deliveryNoteFooterText ?? undefined
        this.downtimePeriods = dto.downtimePeriods?.map(dpDto => new DowntimePeriod(dpDto)) ?? []
        this.articles = dto.articles.map(reservationCartArticleDto => new ReservationCartArticle({
            dto: reservationCartArticleDto
        }))
        this.readyForShipping = dto.readyForShipping
        this.orderConfirmed = dto.orderConfirmed
        this.readyForReturn = dto.readyForReturn
        this.orderClosed = dto.orderClosed
        this.termsOfPaymentCode = dto.termsOfPaymentCode ?? undefined
        this.orderTypeId = dto.orderTypeId ?? undefined
        this.originCountryCode = dto.originCountryCode
        this.targetCountryCode = dto.targetCountryCode
        this.automaticInvoicing = dto.automaticInvoicing ?? null
        this.invoiceRateCalculationType = dto.invoiceRateCalculationType ?? undefined
        this.invoiceBeforeShipping = dto.invoiceBeforeShipping ?? null
        this.useNotReleasedAssets = dto.useNotReleasedAssets
        this.invoicingType = dto.invoicingType ?? null
        this.orderAcknowledgementWillBeRequested = dto.orderAcknowledgementWillBeRequested
        this.referenceNumbers = dto.referenceNumbers
        this.rentToBuy = dto.rentToBuy
        this.commission = dto.commission ?? null
        this.profitCenterId = dto.profitCenterId ?? undefined
        this.invoicing = dto.invoicing
        this.workingDaysCalendarId = dto.workingDaysCalendarId ?? null
        this.workingDaysPerMonth = dto.workingDaysPerMonth ?? null
        this.invoiceIntervalRule = dto.invoiceIntervalRule || undefined
        this.invoiceIntervalRuleCustom = dto.invoiceIntervalRuleCustom || false
        this.invoiceIntervals = dto.invoiceIntervals.map(iiDto => new InvoiceInterval(iiDto))
        this.invoiceBlocked = dto.invoiceBlocked
        this.invoiceBlockedBy = dto.invoiceBlockedBy ?? undefined
        this.invoiceBlockedDate = dto.invoiceBlockedDate ? dayjs(dto.invoiceBlockedDate) : undefined
        this.invoiceBlockedReason = dto.invoiceBlockedReason ?? undefined
        this.machineInsuranceIncluded = dto.machineInsuranceIncluded ?? false
        this.machineInsuranceExcess = dto.machineInsuranceExcess ?? undefined

        this.setLinks(dto.links)
    }

    setLinks(links?: IApiLink[]) {
        if (!links) return
        this.linkUpdate = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCart && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkDelete = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCart && lnk.rel === ApiLinkRel.delete)
        this.linkCancel = links.find(lnk => lnk.entity === ApiLinkEntity.reservation && lnk.rel === ApiLinkRel.cancel)
        this.linkUpdateArticle = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCartArticle && lnk.rel === ApiLinkRel.update)
        this.linkPatchArticle = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCartArticle && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkAddAssets = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCartAsset && lnk.rel === ApiLinkRel.new)
        this.linkUpdateAsset = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCartAsset && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkDeleteAsset = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCartAsset && lnk.rel === ApiLinkRel.delete)
        this.linkEmptyConfiguration = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCartArticle && lnk.rel === ApiLinkRel.delete)
        this.linkTouch = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCartLock && lnk.rel === ApiLinkRel.update)
        this.linkGetShippingOrders = links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrder && lnk.rel === ApiLinkRel.all)
        this.linkCreateDowntimePeriod = links.find(lnk => lnk.entity === ApiLinkEntity.downtimePeriod && lnk.rel === ApiLinkRel.new)
        this.linkCreateInvoicePosition = links.find(lnk => lnk.entity === ApiLinkEntity.invoicePosition && lnk.rel === ApiLinkRel.new)
        this.linkCreateCalculationAdjustment = links.find(lnk => lnk.entity === ApiLinkEntity.calculationAdjustment && lnk.rel === ApiLinkRel.new)
        this.linkGetReservationCartConflicts = links.find(lnk => lnk.entity === ApiLinkEntity.reservationCartConflict && lnk.rel === ApiLinkRel.all)
    }

    printRequestPeriodDates(params?: { withTime?: boolean }) {
        return printPeriod({
            start: this.reservationTime?.reservationStart,
            end: this.reservationTime?.reservationEnd,
            withTime: params?.withTime,
        })
    }

    get hasSalesAndCustomerOrderInfo(): boolean {
        return this.purpose !== Purpose.relocation
    }

    get hasPricing(): boolean {
        return this.purpose !== Purpose.relocation
    }

    get hasTargetCountryField(): boolean {
        return this.hasPricing
    }

    get hasDeliveryTerms(): boolean {
        return this.purpose !== Purpose.relocation
    }

    get hasCreateInvoiceSettings(): boolean {
        return this.automaticInvoicing !== null || this.invoicingType !== null
    }

    get hasInvoicingBySettings(): boolean {
        return this.hasPricing && this.purpose !== Purpose.sale
    }

    get hasInvoiceIntervalSettings(): boolean {
        return this.hasPricing && this.purpose !== Purpose.sale
    }

    get canChangeCreateInvoiceSettings(): boolean {
        return this.hasCreateInvoiceSettings && !this.orderClosed
    }

    get canUpdateReservation(): boolean {
        return !!this.linkUpdate
    }

    get isClosedOrCanceled(): boolean {
        return this.reservationStatus === ReservationStatus.closed || this.reservationStatus === ReservationStatus.canceled
    }

    get isLockedOrClosed(): boolean {
        return !this.canUpdateReservation || this.isClosedOrCanceled
    }

    /**
     * For a sale the start date stays editable until the reservation is locked/closed, independently of the
     * step's editability — but a disposal fixes the period, so it must not be changed anymore. For all other
     * purposes the caller decides via `isEditable`.
     *
     * `disposalDate` is a reservation-level field and is not part of the reservation cart response, so it has
     * to be passed in by the caller (see `$reservationDisposalDate`).
     */
    canChangeStartDate({ isEditable, disposalDate }: { isEditable?: boolean, disposalDate?: Dayjs | null }): boolean {
        return this.purpose === Purpose.sale
            ? !this.isLockedOrClosed && !disposalDate
            : !!isEditable
    }

    get isSalesPersonRequired(): boolean {
        return this.hasSalesAndCustomerOrderInfo && (this.orderConfirmed
            || (this.readyForShipping
                || (this.canCreateOrderAcknowledgement && this.orderAcknowledgementWillBeRequested)
            )
        )
    }

    get hasSalesAdminField(): boolean {
        return this.purpose === Purpose.relocation
    }

    get isSalesAdminRequired(): boolean {
        return (this.hasSalesAndCustomerOrderInfo || this.hasSalesAdminField) && (this.readyForShipping || !!this.invoiceBeforeShipping)
    }

    isDeliveryTermRequired(isReadyForShipping: boolean): boolean {
        return this.hasDeliveryTerms && (isReadyForShipping || !!this.invoiceBeforeShipping)
    }

    get isCustomerOrderDateRequired(): boolean {
        return this.hasSalesAndCustomerOrderInfo
    }

    get areInvoicePositionsRequired(): boolean {
        return this.hasPricing && this.invoicing
            && (this.readyForShipping || this.isNotReservedOrGettingPromoted)
    }

    get hasShortage(): boolean {
        return this.articles.some(a => a.hasShortage)
    }

    get hasUnselectedFixedAssets(): boolean {
        return this.areFixedAssetsRequired && this.articles.some(a => a.hasAssetsNeedingFixedSelection)
    }

    get canSelectWarehouseForLocation() {
        return this.purpose === Purpose.relocation
    }

    getDowntimePeriodsTotalDays() {
        return Reservation.getDowntimePeriodsTotalDays(this.downtimePeriods)
    }

    get downtimePeriodsBoundaries(): { start: Dayjs, end: Dayjs } | null {
        if (!this.downtimePeriods.length) return null
        return {
            start: this.downtimePeriods[this.downtimePeriods.length - 1].start.clone().startOf("day"),
            end: this.downtimePeriods[0].end.clone().endOf("day"),
        }
    }

    isAddressRequired(isReadyForShipping: boolean): boolean {
        return this.reservationStatus !== ReservationStatus.reserved || isReadyForShipping
    }

    get isOrderTypeRequired(): boolean {
        return this.isNotReservedOrGettingPromoted
    }

    get isPaymentTermRequired(): boolean {
        return this.isNotReservedOrGettingPromoted
    }

    getAssetsWithInvalidPeriod(lastNotCreditedInvoiceEndDate: Dayjs | null): IReservationCartAssetWithInvalidPeriod[] {
        let assets: IReservationCartAssetWithInvalidPeriod[] = []
        this.articles.forEach(article => {
            assets = [
                ...assets,
                ...article.getAssetsWithInvalidPeriod({
                    reservationStart: this.reservationTime.reservationStart,
                    reservationEnd: this.reservationTime.reservationEnd || null,
                    lastNotCreditedInvoiceEndDate
                })
            ]
        })
        return assets
    }

    hasInvoicePositionsWithInvalidPeriod(lastNotCreditedInvoiceEndDate?: Dayjs): boolean {
        return this.invoicePositions.some(ip => ip.hasInvalidPeriod(this.reservationTime?.reservationStart, this.reservationTime?.reservationEnd, lastNotCreditedInvoiceEndDate))
    }

    get nodeAsset(): ReservationCartAsset | null {
        const nodeArticle = this.articles.find(a => a.articleId === this.articleId)
        return nodeArticle?.assets[0] || null
    }

    get canChangeOrderConfirmation(): boolean {
        return !!this.purpose && this.purpose !== Purpose.sale
            && !Reservation.isReservationStatusSameOrAfter(this.reservationStatus, ReservationStatus.shipping)
    }

    get canChangeCloseRequest(): boolean {
        return this.reservationStatus === ReservationStatus.finalSettlement
            || this.reservationStatus === ReservationStatus.completed
            || this.reservationStatus === ReservationStatus.invoice
            || this.reservationStatus === ReservationStatus.closed
    }

    get canChangeReadyForReturn(): boolean {
        return !!this.purpose
            && this.purpose !== Purpose.sale
            && (this.reservationStatus === ReservationStatus.active
                || this.reservationStatus === ReservationStatus.return)
    }

    get canChangeReadyForShipping(): boolean {
        return (this.purpose === Purpose.sale && (this.reservationStatus === ReservationStatus.confirmed 
                || (this.orderAcknowledgementWillBeRequested && this.reservationStatus === ReservationStatus.reserved)))
            || (!!this.purpose && this.purpose !== Purpose.sale && (this.reservationStatus === ReservationStatus.reserved || this.reservationStatus === ReservationStatus.confirmed))
    }

    get canCreateOrderAcknowledgement() {
        return this.purpose === Purpose.sale
    }

    get canCreateRentalContract() {
        return this.purpose === Purpose.rent
    }

    get canChangeReferenceNumbersUpdateType(): boolean {
        return !Reservation.isReservationStatusSameOrAfter(
            this.reservationStatus,
            this.purpose === Purpose.sale ? ReservationStatus.invoice : ReservationStatus.active,
        )
    }

    /**
     * Whether the "use reference numbers of the first asset in configuration" approach may still be toggled.
     *
     * Unlike {@link canChangeReferenceNumbersUpdateType} the backend allows this only before the request
     * becomes active, for sale requests as well.
     */
    get canChangeUseFirstAssetOfConfiguration(): boolean {
        return !Reservation.isReservationStatusSameOrAfter(
            this.reservationStatus,
            ReservationStatus.active,
        )
    }

    get hasRentToBuyFormSection(): boolean {
        return this.purpose === Purpose.rent
            || this.purpose === Purpose.sale
    }

    get hasRentToBuySummarySection(): boolean {
        return (this.purpose === Purpose.rent && (!!this.rentToBuy.mark || !!this.rentToBuy.relatedReservationId || !!this.rentToBuy.changeAssetTypeOnRelease))
            || (this.purpose === Purpose.sale && !!this.rentToBuy.relatedReservationId)
    }

    get hasPricingRentToByFields(): boolean {
        return (this.purpose === Purpose.rent && !!this.rentToBuy.mark)
            || (this.purpose === Purpose.sale && !!this.rentToBuy.relatedReservationId)
    }

    get canEditPricingRentToBuyFields(): boolean {
        return this.hasPricingRentToByFields && this.purpose === Purpose.rent
    }

    get areFixedAssetsRequired(): boolean {
        return this.purpose === Purpose.sale
    }

    get isNotReservedOrGettingPromoted(): boolean {
        return this.reservationStatus !== ReservationStatus.reserved || (this.canCreateOrderAcknowledgement && this.orderAcknowledgementWillBeRequested)
    }

    get shouldShowInvoicingSwitch(): boolean {
        return this.purpose === Purpose.demo
            || this.purpose === Purpose.internalUse
            || this.purpose === Purpose.bridge
    }

    /**
     * The effective request currency: the value persisted on the cart, falling back to the customer-based
     * derivation for responses without the field.
     */
    get requestCurrency(): Currency | null {
        return this.currency ?? getCustomerBasedCurrency({
            hasCustomer: !!this.customer,
            customerCurrency: this.customer?.currency,
        })
    }

    get shouldShowCurrencyChangeWarning(): boolean {
        return !!(
            this.requestCurrency
            && this.currencyChangedWhileHavingInvoicePositions
            && this.requestCurrency !== this.initialCurrency
            && this.invoicePositions.length
        )
    }
}
